import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { UIDAPresentationTypeEnum } from '@kaltura-ott/tvpil';
import { Linear, ParentalPinType } from '@kaltura-ott/tvpil-shared';

import { DescriptionWithAge } from 'components/widgets/Modal/PinCodeModal/DescriptionWithAge/DescriptionWithAge';
import PinCodeModal from 'components/widgets/Modal/PinCodeModal/PinCodeModal';
import { PinCodeModalTypes } from 'components/widgets/Modal/PinCodeModal/PinCodeModalTypesEnum';
import { useModalContext } from 'context/modal/modal';
import { useAppStore, useParentalPinStore } from 'hooks';
import routes from 'routesPaths';

interface Props {
  isContentBlockedAsAdult: boolean;
  program: any;
  assetId: number;
  parentalRating: number;
  playableAsset: Linear;
}

export const useWatchNowHandler = ({
  isContentBlockedAsAdult,
  program,
  assetId,
  parentalRating,
  playableAsset,
}: Props) => {
  const modalContext = useModalContext();
  const {
    appStore: { lastWatchedLinear },
  } = useAppStore();
  const parentalPinStore = useParentalPinStore();
  const mediaType = program?.data?.mediaType?.toLowerCase();
  const isContentBlockedByAge = !!parentalRating && !!program && parentalPinStore.isBlockByParentRating(program);
  const navigate = useNavigate();

  return useCallback(() => {
    if (isContentBlockedAsAdult) {
      const validateAdult = (pin: string) => parentalPinStore.validatePin(pin, ParentalPinType.adult, program);

      modalContext.openModal(
        <PinCodeModal
          context={UIDAPresentationTypeEnum.applications}
          type={PinCodeModalTypes.RestrictedContent}
          validatePin={validateAdult}
        />,
      );
      return;
    }

    if (isContentBlockedByAge) {
      const validateParental = (pin: string) => parentalPinStore.validatePin(pin, ParentalPinType.parental, program);

      modalContext.openModal(
        <PinCodeModal
          callback={(success: boolean) => {
            if (success) {
              navigate(
                routes.live.link(assetId!, mediaType!, lastWatchedLinear.mainHeroRail?.lastWatchedSubLinearExternalId),
              );
            }
          }}
          context={UIDAPresentationTypeEnum.applications}
          description={parentalRating ? <DescriptionWithAge age={parentalRating} /> : undefined}
          type={PinCodeModalTypes.RestrictedContentParental}
          validatePin={validateParental}
        />,
      );
      return;
    }

    navigate(routes.live.link(assetId!, mediaType!, lastWatchedLinear.mainHeroRail?.lastWatchedSubLinearExternalId));
  }, [
    assetId,
    isContentBlockedAsAdult,
    isContentBlockedByAge,
    modalContext,
    parentalRating,
    mediaType,
    playableAsset,
    lastWatchedLinear.mainHeroRail,
  ]);
};
